import type { Workbook, WorkbookListItem } from '@mathflat/domain/Workbook'
import { makeAutoObservable } from 'mobx'

import type CheckableMap from '@/models/CheckableMap'
import { typeSafeAssign } from '@/utils/common'

export class WorkbookColl<T extends WorkbookListItem = WorkbookListItem> {
  constructor(private workbookMap: CheckableMap<T['id'], T>) {
    makeAutoObservable(this)
  }

  get workSheetList() {
    return [...this.workbookMap.values()]
  }

  get origin() {
    return this.workbookMap
  }

  get toArr() {
    return [...this.workbookMap.values()]
  }

  get entries() {
    return this.workbookMap.entries()
  }

  get checkedIds() {
    return this.workbookMap.toCheckedKeys
  }

  get isEmpty() {
    return this.workbookMap.isEmpty
  }

  get isNotEmpty() {
    return this.workbookMap.isNotEmpty
  }

  values() {
    return this.workbookMap.values()
  }

  adds(workbookColl: WorkbookColl<T>) {
    this.workbookMap.adds(workbookColl.workbookMap.entries())
  }

  set(k, v) {
    return this.workbookMap.set(k, v)
  }

  delete(id) {
    return this.workbookMap.delete(id)
  }

  deletes(ids) {
    return this.workbookMap.deletes(ids)
  }

  get checkSize() {
    return this.workbookMap.checkSize
  }

  get toCheckedValues() {
    return this.workbookMap.toCheckedValues
  }

  toCheckedSize() {
    return this.workbookMap.toCheckedValues.length
  }

  get(id: Workbook['id']) {
    return this.workbookMap.get(id)
  }

  setCheck(v: T['id']) {
    this.workbookMap.setCheck(v)
  }

  deleteCheck(v: T['id']) {
    this.workbookMap.deleteCheck(v)
  }

  clearAllCheck() {
    this.workbookMap.clearAllCheck()
  }

  isChecked(v: T['id']) {
    return this.workbookMap.isChecked(v)
  }

  get isPartChecked() {
    return this.workbookMap.isPartChecked
  }

  toggleCheck(v: T['id']) {
    this.workbookMap.toggleCheck(v)
  }

  updateWorkbook(id: Workbook['id'], updateData: WorkbookListItem) {
    const selected = this.workbookMap?.get(id) as WorkbookListItem
    if (selected) {
      typeSafeAssign(selected, updateData)
    }
  }
}
